import { useEffect, useMemo, useState, type FormEvent } from "react";
import { AiClientError, sendAiChat } from "../../api/ai-client";
import type { AiChatResponse, ConsentSnapshot } from "../../api/ai.types";
import { getOrCreateAuthSession } from "../../api/auth-client";
import type { AuthSessionResponse } from "../../api/auth.types";
import { createImage4KJob, getMediaJob, type GeneratedImage4K } from "../../api/media-client";
import { getActiveUltraOrganizationId } from "../../api/ultra-client";
import {
  renderPresentationVideo4K,
  type PresentationVideoResult
} from "../../media/presentation-video";
import type { OmnisyniaClientModule } from "../module.types";

type AspectRatio = "16:9" | "9:16" | "1:1";

interface RenderedPresentationVideo extends Omit<PresentationVideoResult, "blob"> {
  readonly url: string;
  readonly fileName: string;
}

const REQUIRED_RESOLUTION = "4K" as const;

const tools = [
  "Línea de tiempo multicapa",
  "Recorte y transiciones",
  "Audio, voz y subtítulos",
  "Entrega 4K verificada"
] as const;

function SocialCutPro() {
  const [projectName, setProjectName] = useState("Proyecto OMNISYNIA");
  const resolution = REQUIRED_RESOLUTION;
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("16:9");
  const [draftMessage, setDraftMessage] = useState<string | null>(null);
  const [prompt, setPrompt] = useState(
    "Prepara un vídeo premium para presentar OMNISYNIA con subtítulos, ritmo moderno y exportación nativa."
  );
  const [consent, setConsent] = useState<ConsentSnapshot>({
    privacyPolicyAccepted: false,
    termsAccepted: false,
    aiDisclosureAccepted: false,
    safetyPolicyAccepted: false,
    memoryOptIn: false
  });
  const [aiResponse, setAiResponse] = useState<AiChatResponse | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [session, setSession] = useState<AuthSessionResponse | null>(null);
  const [imagePrompt, setImagePrompt] = useState(
    "Crea una imagen publicitaria futurista y elegante para lanzar OMNISYNIA en redes sociales."
  );
  const [imageJobStatus, setImageJobStatus] = useState<string | null>(null);
  const [generatedImage, setGeneratedImage] = useState<GeneratedImage4K | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isRenderingVideo, setIsRenderingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoStatus, setVideoStatus] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [renderedVideo, setRenderedVideo] = useState<RenderedPresentationVideo | null>(null);

  useEffect(() => {
    void getOrCreateAuthSession().then(setSession).catch(() => setSession(null));
  }, []);

  useEffect(() => () => {
    if (renderedVideo) URL.revokeObjectURL(renderedVideo.url);
  }, [renderedVideo]);

  const projectSummary = useMemo(() => {
    const dimensions = aspectRatio === "16:9"
      ? "3840×2160"
      : aspectRatio === "9:16"
        ? "2160×3840"
        : "2160×2160";
    return `${resolution} · ${dimensions} · ${aspectRatio} · 30 FPS`;
  }, [aspectRatio, resolution]);
  const coreConsentReady =
    consent.privacyPolicyAccepted &&
    consent.termsAccepted &&
    consent.aiDisclosureAccepted &&
    consent.safetyPolicyAccepted;

  function createDraft(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const safeName = projectName.trim() || "Proyecto sin título";
    setDraftMessage(`Borrador “${safeName}” preparado en ${projectSummary}.`);
  }

  async function askAiEngine(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!coreConsentReady) {
      setAiError("Para usar el motor IA acepta privacidad, términos, aviso IA y normas de seguridad.");
      return;
    }

    setIsAiLoading(true);
    setAiError(null);
    setAiResponse(null);

    try {
      const response = await sendAiChat({
        moduleId: "socialcut-pro",
        messages: [
          {
            role: "user",
            content: [
              prompt,
              `Proyecto: ${projectName.trim() || "Proyecto sin título"}.`,
              `Perfil: ${projectSummary}.`
            ].join("\n")
          }
        ],
        metadata: {
          locale: "es",
          platform: "web"
        },
        userConsent: consent
      });
      setAiResponse(response);
    } catch (error) {
      setAiError(
        error instanceof AiClientError
          ? `${error.message} (${error.code})`
          : "No se pudo conectar con el motor IA."
      );
    } finally {
      setIsAiLoading(false);
    }
  }

  function toggleConsent(key: keyof ConsentSnapshot) {
    setConsent((currentConsent) => ({
      ...currentConsent,
      [key]: !currentConsent[key]
    }));
  }

  async function generateAdvertisingImage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!coreConsentReady) {
      setImageError("Acepta privacidad, términos, aviso IA y normas de seguridad.");
      return;
    }
    const organizationId = getActiveUltraOrganizationId();
    if (!session || !organizationId) {
      setImageError("Entra en tu cuenta Ultra y activa IA OMNISYNIA antes de generar.");
      return;
    }

    setIsGeneratingImage(true);
    setImageError(null);
    setGeneratedImage(null);
    setRenderedVideo(null);
    setVideoStatus(null);
    setVideoError(null);
    setVideoProgress(0);
    setImageJobStatus("Preparando trabajo 4K...");
    try {
      let job = await createImage4KJob({
        token: session.sessionToken,
        organizationId,
        prompt: imagePrompt,
        projectName,
        aspectRatio
      });
      for (let attempt = 0; attempt < 90 && (job.status === "queued" || job.status === "running"); attempt += 1) {
        setImageJobStatus(job.status === "queued" ? "En cola segura..." : "Generando imagen 4K...");
        await new Promise((resolve) => window.setTimeout(resolve, 2_000));
        job = await getMediaJob(session.sessionToken, job.id);
      }
      if (job.status === "completed" && job.result) {
        setGeneratedImage(job.result);
        setImageJobStatus(`4K verificado · ${job.result.width}×${job.result.height}`);
      } else if (job.status === "failed") {
        throw new Error(job.error ?? "La generación no pudo completarse.");
      } else {
        throw new Error("La generación continúa tardando demasiado. Inténtalo de nuevo.");
      }
    } catch (error) {
      setImageError(error instanceof Error ? error.message : "No se pudo generar la imagen.");
      setImageJobStatus(null);
    } finally {
      setIsGeneratingImage(false);
    }
  }

  async function generatePresentationVideo() {
    if (!generatedImage) {
      setVideoError("Primero genera una imagen publicitaria 4K para usarla en la presentación.");
      return;
    }

    setIsRenderingVideo(true);
    setVideoProgress(0);
    setVideoError(null);
    setVideoStatus("Preparando el Estudio de Vídeo 4K...");
    try {
      const result = await renderPresentationVideo4K({
        projectName,
        imageBase64: generatedImage.base64,
        imageMimeType: generatedImage.mimeType,
        aspectRatio,
        onProgress: (percent) => {
          setVideoProgress(percent);
          setVideoStatus(percent < 100
            ? `Renderizando presentación 4K · ${percent}%`
            : "Finalizando archivo de vídeo...");
        }
      });
      const safeName = projectName.trim().replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "omnisynia";
      const url = URL.createObjectURL(result.blob);
      setRenderedVideo({
        url,
        fileName: `${safeName}-presentacion-4k.${result.extension}`,
        mimeType: result.mimeType,
        extension: result.extension,
        width: result.width,
        height: result.height,
        durationSeconds: result.durationSeconds
      });
      setVideoStatus(`Vídeo 4K terminado · ${result.width}×${result.height} · ${result.durationSeconds} s`);
    } catch (error) {
      setVideoError(error instanceof Error ? error.message : "No se pudo crear el vídeo 4K.");
      setVideoStatus(null);
    } finally {
      setIsRenderingVideo(false);
    }
  }

  return (
    <section className="editor-shell" aria-labelledby="socialcut-title">
      <header className="editor-header">
        <div>
          <p className="eyebrow">VÍDEO PREMIUM · MÓDULO INICIAL</p>
          <h2 id="socialcut-title">OMNISYNIA SocialCut Pro</h2>
          <p>
            Base desacoplada para edición profesional, preparada para conectar motores de
            renderizado desde el backend y entregar siempre el archivo final en 4K.
          </p>
        </div>
        <span className="status-pill">CORE READY</span>
      </header>

      <div className="editor-grid">
        <form className="project-panel" onSubmit={createDraft}>
          <label>
            Nombre del proyecto
            <input
              value={projectName}
              onChange={(event) => setProjectName(event.target.value)}
              maxLength={80}
            />
          </label>

          <div className="field-row">
            <label>
              Resolución final
              <input readOnly value="4K Ultra HD · obligatoria" />
            </label>
            <label>
              Formato
              <select
                value={aspectRatio}
                onChange={(event) => setAspectRatio(event.target.value as AspectRatio)}
              >
                <option value="16:9">16:9 Horizontal</option>
                <option value="9:16">9:16 Vertical</option>
                <option value="1:1">1:1 Cuadrado</option>
              </select>
            </label>
          </div>

          <button className="primary-button" type="submit">
            Crear borrador
          </button>
          {draftMessage ? <p className="success-message">{draftMessage}</p> : null}
        </form>

        <div className="preview-panel" aria-label="Vista previa de vídeo">
          <div className="preview-orbit" aria-hidden="true">
            <span>▶</span>
          </div>
          <strong>Vista previa segura</strong>
          <small>{projectSummary}</small>
        </div>
      </div>

      <div className="tool-grid">
        {tools.map((tool, index) => (
          <article key={tool}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <p>{tool}</p>
          </article>
        ))}
      </div>

      <form className="ai-panel" onSubmit={askAiEngine}>
        <div className="ai-panel-header">
          <div>
            <p className="eyebrow">MOTOR IA CONECTADO</p>
            <h3>Habla con OMNISYNIA para preparar el vídeo</h3>
          </div>
          <span className={coreConsentReady ? "status-pill" : "status-pill warning"}>
            {coreConsentReady ? "CONSENT OK" : "CONSENT REQUIRED"}
          </span>
        </div>

        <label>
          Prompt para el motor IA
          <textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            maxLength={1200}
            rows={5}
          />
        </label>

        <div className="consent-grid" aria-label="Consentimientos obligatorios">
          <label className="consent-check">
            <input
              checked={consent.privacyPolicyAccepted}
              onChange={() => toggleConsent("privacyPolicyAccepted")}
              type="checkbox"
            />
            Acepto la política de privacidad.
          </label>
          <label className="consent-check">
            <input
              checked={consent.termsAccepted}
              onChange={() => toggleConsent("termsAccepted")}
              type="checkbox"
            />
            Acepto los términos de uso.
          </label>
          <label className="consent-check">
            <input
              checked={consent.aiDisclosureAccepted}
              onChange={() => toggleConsent("aiDisclosureAccepted")}
              type="checkbox"
            />
            Entiendo que la respuesta puede usar IA.
          </label>
          <label className="consent-check">
            <input
              checked={consent.safetyPolicyAccepted}
              onChange={() => toggleConsent("safetyPolicyAccepted")}
              type="checkbox"
            />
            Acepto las normas de seguridad.
          </label>
          <label className="consent-check optional">
            <input
              checked={consent.memoryOptIn}
              onChange={() => toggleConsent("memoryOptIn")}
              type="checkbox"
            />
            Permitir memoria para esta interacción.
          </label>
        </div>

        <button className="primary-button" disabled={isAiLoading || !prompt.trim()} type="submit">
          {isAiLoading ? "Consultando motor IA..." : "Enviar al motor IA"}
        </button>

        {aiError ? <p className="error-message">{aiError}</p> : null}
        {aiResponse ? (
          <article className="ai-response" aria-label="Respuesta del motor IA">
            <header>
              <span>{aiResponse.provider}</span>
              <span>{aiResponse.route.moduleId}</span>
              <span>Core: {aiResponse.core.intent.category}</span>
              <span>{aiResponse.core.model.adapter}</span>
              <span>Sesión: {aiResponse.session.userMode}</span>
              <span>
                Memoria: {aiResponse.safety.memoryAllowed ? "activada" : "apagada"}
              </span>
            </header>
            <p>{aiResponse.message.content}</p>
          </article>
        ) : null}
      </form>

      <form className="ai-panel image-generation-panel" onSubmit={generateAdvertisingImage}>
        <div className="ai-panel-header">
          <div>
            <p className="eyebrow">GENERADOR REAL · GPT-IMAGE-2</p>
            <h3>Imagen publicitaria 4K</h3>
          </div>
          <span className="status-pill">10 CRÉDITOS · FUNDADORES SIN LÍMITE</span>
        </div>
        <label>
          Describe la imagen de la campaña
          <textarea
            value={imagePrompt}
            onChange={(event) => setImagePrompt(event.target.value)}
            maxLength={32000}
            rows={5}
          />
        </label>
        <button
          className="primary-button"
          disabled={isGeneratingImage || !imagePrompt.trim()}
          type="submit"
        >
          {isGeneratingImage ? "Generando 4K..." : "Generar imagen publicitaria 4K"}
        </button>
        {imageJobStatus ? <p className="success-message">{imageJobStatus}</p> : null}
        {imageError ? <p className="error-message">{imageError}</p> : null}
        {generatedImage ? (
          <article className="generated-image-card">
            <img
              alt={`Imagen publicitaria 4K de ${projectName}`}
              src={`data:${generatedImage.mimeType};base64,${generatedImage.base64}`}
            />
            <a
              className="secondary-button generated-image-download"
              download={`${projectName.trim().replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "omnisynia"}-4k.png`}
              href={`data:${generatedImage.mimeType};base64,${generatedImage.base64}`}
            >
              Descargar PNG 4K
            </a>
          </article>
        ) : null}
      </form>

      <section className="ai-panel video-render-panel" aria-labelledby="video-studio-title">
        <div className="ai-panel-header">
          <div>
            <p className="eyebrow">ESTUDIO DE VÍDEO · RENDER LOCAL</p>
            <h3 id="video-studio-title">Presentación publicitaria 4K</h3>
          </div>
          <span className="status-pill">SIN CRÉDITOS EXTRA</span>
        </div>
        <p className="video-render-description">
          Convierte la imagen generada en una presentación animada con identidad OMNISYNIA.
          Mantén esta pestaña abierta mientras se completa el render.
        </p>
        <button
          className="primary-button"
          disabled={isRenderingVideo || !generatedImage}
          onClick={() => void generatePresentationVideo()}
          type="button"
        >
          {isRenderingVideo ? `Creando vídeo 4K · ${videoProgress}%` : "Crear presentación en vídeo 4K"}
        </button>
        {isRenderingVideo ? (
          <progress aria-label="Progreso del vídeo" max={100} value={videoProgress} />
        ) : null}
        {videoStatus ? <p className="success-message">{videoStatus}</p> : null}
        {videoError ? <p className="error-message">{videoError}</p> : null}
        {renderedVideo ? (
          <article className="generated-video-card">
            <video controls playsInline preload="metadata" src={renderedVideo.url}>
              Tu navegador no puede reproducir este vídeo.
            </video>
            <div className="generated-video-meta">
              <span>{renderedVideo.width}×{renderedVideo.height}</span>
              <span>{renderedVideo.durationSeconds} segundos</span>
              <span>{renderedVideo.extension.toUpperCase()}</span>
            </div>
            <a
              className="secondary-button generated-image-download"
              download={renderedVideo.fileName}
              href={renderedVideo.url}
            >
              Descargar vídeo 4K
            </a>
          </article>
        ) : null}
      </section>
    </section>
  );
}

export const socialCutProClientModule: OmnisyniaClientModule = {
  id: "socialcut-pro",
  displayName: "SocialCut Pro",
  description: "Editor profesional modular de Vídeo Premium.",
  tier: "premium",
  status: "available",
  Component: SocialCutPro
};
